import { useEffect } from 'react';

import { useContent } from '../content/ContentProvider';

/**
 * Sets the document title, meta description, and canonical URL for a page.
 *
 * Pass a `slug` and the title and description come from Yoast, over REST, which
 * is where the PHP site gets them too. The `title` and `description` props are
 * only fallbacks for when a page has no Yoast entry (the 404 route) or the
 * request has not landed yet.
 *
 * That indirection is the point. An earlier version hard-coded titles here, and
 * they were markedly worse than the Yoast ones already serving on the live site:
 * "Gallery — Top Tile Construction" against "Our Work: Tile, Flooring & Painting
 * Photos | Edmonton". Every page had quietly lost the city and the service
 * keywords. Reading them from one place means that cannot happen again, and the
 * owner can edit a title in wp-admin and have both front ends follow.
 *
 * A caveat that has not changed: this runs in the browser, after load. Google
 * renders JavaScript and will usually pick these up, but the crawlers behind
 * link previews in Facebook, WhatsApp, LinkedIn, and Slack do not — they read
 * the HTML as served and see the static tags in index.html. Fixing that properly
 * means pre-rendering each route at build time. See the SEO section in
 * docs/README-frontend.md.
 *
 * @param {object} options
 * @param {string} [options.slug]        Page slug as WordPress knows it.
 * @param {string} [options.title]       Fallback title.
 * @param {string} [options.description] Fallback meta description.
 */
export default function usePageMeta({ slug, title, description }) {
	const { data } = useContent();

	const fromWordPress = slug ? data?.pages?.[slug] : undefined;
	const resolvedTitle = fromWordPress?.title || title;
	const resolvedDescription = fromWordPress?.description || description;

	useEffect(() => {
		if (resolvedTitle) {
			document.title = resolvedTitle;
		}

		if (resolvedDescription) {
			let tag = document.querySelector('meta[name="description"]');

			if (!tag) {
				tag = document.createElement('meta');
				tag.setAttribute('name', 'description');
				document.head.appendChild(tag);
			}

			tag.setAttribute('content', resolvedDescription);
		}

		// One canonical URL per page, without query strings or hashes, so the
		// same content reached two ways is not indexed twice.
		let canonical = document.querySelector('link[rel="canonical"]');

		if (!canonical) {
			canonical = document.createElement('link');
			canonical.setAttribute('rel', 'canonical');
			document.head.appendChild(canonical);
		}

		canonical.setAttribute('href', window.location.origin + window.location.pathname);
	}, [resolvedTitle, resolvedDescription]);
}
